<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TransactionNotesEditor',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'update:modelValue']);

  const props = defineProps({
    ...userComponentProps,
    modelValue: {
      type: Array,
      required: false,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItems = (...args) => {
    const value = props.modelValue;
    if (value == null || value.length == 0) {
      return [];
    }
    return value;
  };
  const addItem = (...args) => {
    const newItems = [...items.value, newItem()];
    items.value = newItems;
    emitChange();
  };
  const newItem = (...args) => {
    return {
      message: null,
      valid_from: null,
      valid_to: null,
      counter: null,
      max_displays: null,
    };
  };
  const removeItem = (...args) => {
    const [item] = args;
    const newItems = items.value.filter((p) => p !== item);
    if (newItems.length !== items.value.length) {
      items.value = newItems;
      emitChange();
    }
  };
  const emitChange = (...args) => {
    emit('update:modelValue', items.value);
  };

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = () => {
    return getItems();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );

  const act_5534025912102772_script_vmyysv1ea_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_5534025912102772_script_vmyysv1ea_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5534025912102772_script_vmyysv1ea_value_callable($event);
      },
    };
  };
  const act_309515315410323_script_7npphzkqs_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_309515315410323_script_7npphzkqs_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_309515315410323_script_7npphzkqs_value_callable($event);
      },
    };
  };
  const act_5188750589197515_script_rrmi6c2li_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_5188750589197515_script_rrmi6c2li_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5188750589197515_script_rrmi6c2li_value_callable($event);
      },
    };
  };
  const act_3475726502813997_script_8kggi0u5f_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_3475726502813997_script_8kggi0u5f_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3475726502813997_script_8kggi0u5f_value_callable($event);
      },
    };
  };
  const act_4449693688767071_script_twsbd6vw2_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_4449693688767071_script_twsbd6vw2_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4449693688767071_script_twsbd6vw2_value_callable($event);
      },
    };
  };
  const act_1548630417156826_script_akpxji9fd_value_callable = async (
    $event
  ) => {
    return removeItem($event.scope.item);
  };
  const act_1548630417156826_script_akpxji9fd_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1548630417156826_script_akpxji9fd_value_callable($event);
      },
    };
  };
  const act_96700093353179_script_uq1sv3dbi_value_callable = async ($event) => {
    return addItem();
  };
  const act_96700093353179_script_uq1sv3dbi_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_96700093353179_script_uq1sv3dbi_value_callable($event);
      },
    };
  };

  const act_5534025912102772_script_vmyysv1ea = new actionTypes.ScriptAction({
    actionArgs: act_5534025912102772_script_vmyysv1ea_getActionArgs,
  });
  const inst_evh_5534025912102772_input_6piu56zhs =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_script_vmyysv1ea,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_5534025912102772_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_input_6piu56zhs],
    event: 'input',
  });
  const act_309515315410323_script_7npphzkqs = new actionTypes.ScriptAction({
    actionArgs: act_309515315410323_script_7npphzkqs_getActionArgs,
  });
  const inst_evh_309515315410323_input_oymu23lxl =
    new actionTypes.EventHandlerImpl({
      action: act_309515315410323_script_7npphzkqs,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_309515315410323_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_309515315410323_input_oymu23lxl],
    event: 'input',
  });
  const act_5188750589197515_script_rrmi6c2li = new actionTypes.ScriptAction({
    actionArgs: act_5188750589197515_script_rrmi6c2li_getActionArgs,
  });
  const inst_evh_5188750589197515_input_1b2apf103 =
    new actionTypes.EventHandlerImpl({
      action: act_5188750589197515_script_rrmi6c2li,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_5188750589197515_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5188750589197515_input_1b2apf103],
    event: 'input',
  });
  const act_3475726502813997_script_8kggi0u5f = new actionTypes.ScriptAction({
    actionArgs: act_3475726502813997_script_8kggi0u5f_getActionArgs,
  });
  const inst_evh_3475726502813997_input_vj8muhbwg =
    new actionTypes.EventHandlerImpl({
      action: act_3475726502813997_script_8kggi0u5f,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_3475726502813997_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3475726502813997_input_vj8muhbwg],
    event: 'input',
  });
  const act_4449693688767071_script_twsbd6vw2 = new actionTypes.ScriptAction({
    actionArgs: act_4449693688767071_script_twsbd6vw2_getActionArgs,
  });
  const inst_evh_4449693688767071_input_2umnrtxf4 =
    new actionTypes.EventHandlerImpl({
      action: act_4449693688767071_script_twsbd6vw2,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_4449693688767071_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4449693688767071_input_2umnrtxf4],
    event: 'input',
  });
  const act_1548630417156826_script_akpxji9fd = new actionTypes.ScriptAction({
    actionArgs: act_1548630417156826_script_akpxji9fd_getActionArgs,
  });
  const inst_evh_1548630417156826_clickPreventStop_poufd4y6a =
    new actionTypes.EventHandlerImpl({
      action: act_1548630417156826_script_akpxji9fd,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_1548630417156826_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1548630417156826_clickPreventStop_poufd4y6a],
      event: 'click.prevent.stop',
    });
  const act_96700093353179_script_uq1sv3dbi = new actionTypes.ScriptAction({
    actionArgs: act_96700093353179_script_uq1sv3dbi_getActionArgs,
    displayName: 'addItem',
  });
  const inst_evh_96700093353179_clickPreventStop_19ufek0w2 =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_script_uq1sv3dbi,
      event: 'click.prevent.stop',
      displayName: 'addItem',
    });
  const inst_evh_96700093353179_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_96700093353179_clickPreventStop_19ufek0w2],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_5534025912102772_input_6piu56zhs,
    inst_evh_5534025912102772_input,
    inst_evh_309515315410323_input_oymu23lxl,
    inst_evh_309515315410323_input,
    inst_evh_5188750589197515_input_1b2apf103,
    inst_evh_5188750589197515_input,
    inst_evh_3475726502813997_input_vj8muhbwg,
    inst_evh_3475726502813997_input,
    inst_evh_4449693688767071_input_2umnrtxf4,
    inst_evh_4449693688767071_input,
    inst_evh_1548630417156826_clickPreventStop_poufd4y6a,
    inst_evh_1548630417156826_clickPreventStop,
    inst_evh_96700093353179_clickPreventStop_19ufek0w2,
    addItem: inst_evh_96700093353179_clickPreventStop_19ufek0w2,
    inst_evh_96700093353179_clickPreventStop,
  };

  const evh_5534025912102772_input_6piu56zhs = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_input_6piu56zhs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_input = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_input_oymu23lxl = async (event, scope) => {
    return await actions.inst_evh_309515315410323_input_oymu23lxl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_input = async (event, scope) => {
    return await actions.inst_evh_309515315410323_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5188750589197515_input_1b2apf103 = async (event, scope) => {
    return await actions.inst_evh_5188750589197515_input_1b2apf103.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5188750589197515_input = async (event, scope) => {
    return await actions.inst_evh_5188750589197515_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3475726502813997_input_vj8muhbwg = async (event, scope) => {
    return await actions.inst_evh_3475726502813997_input_vj8muhbwg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3475726502813997_input = async (event, scope) => {
    return await actions.inst_evh_3475726502813997_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4449693688767071_input_2umnrtxf4 = async (event, scope) => {
    return await actions.inst_evh_4449693688767071_input_2umnrtxf4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4449693688767071_input = async (event, scope) => {
    return await actions.inst_evh_4449693688767071_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1548630417156826_clickPreventStop_poufd4y6a = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1548630417156826_clickPreventStop_poufd4y6a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1548630417156826_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1548630417156826_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop_19ufek0w2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_96700093353179_clickPreventStop_19ufek0w2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_96700093353179_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div :key="index" class="d-flex flex-col row" v-for="(item, index) in items">
    <div class="col-auto" v-if="items.length > 1">#{{ index + 1 }}</div>
    <div class="col">
      <div class="mb-2">
        <ItpFormInput
          name="message"
          v-model="item.message"
          required
          :readonly="readonly"
          :title="
            pgettext(
              'TransactionNotesEditor',
              'The message to include to the invoice.'
            )
          "
          @input="evh_5534025912102772_input($event, { item, index })"
        >
        </ItpFormInput>
      </div>
      <div class="row align-items-center">
        <div class="col-3">
          <ItpFormDatetime
            name="valid_from"
            v-model="item.valid_from"
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Valid from')"
            :title="pgettext('TransactionNotesEditor', 'Valid from')"
            @input="evh_309515315410323_input($event, { item, index })"
          >
          </ItpFormDatetime>
        </div>
        <div class="col-3">
          <ItpFormDatetime
            name="valid_to"
            v-model="item.valid_to"
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Valid to')"
            :title="pgettext('TransactionNotesEditor', 'Valid to')"
            @input="evh_5188750589197515_input($event, { item, index })"
          >
          </ItpFormDatetime>
        </div>
        <div class="col-3">
          <ItpFormInput
            name="counter"
            type="number"
            v-model="item.counter"
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Counter')"
            :title="pgettext('TransactionNotesEditor', 'Counter')"
            @input="evh_3475726502813997_input($event, { item, index })"
          >
          </ItpFormInput>
        </div>
        <div class="col-3">
          <ItpFormInput
            name="max_displays"
            type="number"
            v-model="item.max_displays"
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Max displays')"
            :title="pgettext('TransactionNotesEditor', 'Max displays')"
            @input="evh_4449693688767071_input($event, { item, index })"
          >
          </ItpFormInput>
        </div>
      </div>
    </div>
    <div class="col-auto" v-if="!readonly && !disabled">
      <button
        data-bs-dismiss="modal"
        aria-label="Close"
        :title="pgettext('TransactionNotesEditor', 'Remove')"
        class="close"
        @click.prevent.stop="
          evh_1548630417156826_clickPreventStop($event, { item, index })
        "
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>
  <div class="d-flex mt-2 align-items-center" v-if="!readonly && !disabled">
    <div @click.prevent.stop="evh_96700093353179_clickPreventStop($event, {})">
      <ItpLink
        :icon="$config.addIcon"
        :text="pgettext('TransactionNotesEditor', 'Add')"
      >
      </ItpLink>
    </div>
  </div>
</template>
